import type {
  FormFieldProps,
  SelectFieldProps,
  TextareaFieldProps,
  TextFieldProps,
} from "./FormField";

// src/components/common/Forms/index.ts
export {default as FormField} from "./FormField";
export {default as FormModal} from "./FormModal";
export type {FormFieldProps} from "./FormField";

// Common form validation helpers
export const validationRules = {
  required: {required: true},
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  phone: {
    pattern: /^09\d{7,9}$/,
  },
  password: {
    required: true,
    minLength: 8,
    custom: (value: unknown) => {
      const password = value as string;
      if (!password) return null;

      const hasUpper = /[A-Z]/.test(password);
      const hasLower = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);

      if (!hasUpper || !hasLower || !hasNumber) {
        return "Password must contain uppercase, lowercase, and number";
      }
      return null;
    },
  },
  url: {
    pattern: /^https?:\/\/.+/,
  },
  positiveNumber: {
    min: 0,
  },
  maxLength: (length: number) => ({
    maxLength: length,
  }),
  minLength: (length: number) => ({
    minLength: length,
  }),
  range: (min: number, max: number) => ({
    min,
    max,
  }),
};

// Common field configurations
export const commonFields = {
  email: (
    name = "email",
    label = "Email"
  ): Omit<TextFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "email",
    required: true,
    autoComplete: "email",
  }),

  password: (
    name = "password",
    label = "Password"
  ): Omit<TextFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "password",
    required: true,
    autoComplete: "new-password",
  }),

  confirmPassword: (
    name = "confirmPassword",
    label = "Confirm Password"
  ): Omit<TextFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "password",
    required: true,
    autoComplete: "new-password",
  }),

  phone: (
    name = "phone",
    label = "Phone"
  ): Omit<TextFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "tel",
    placeholder: "09xxxxxxxxx",
    autoComplete: "tel",
  }),

  name: (
    name = "name",
    label = "Name"
  ): Omit<TextFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "text",
    required: true,
    autoComplete: "name",
  }),

  title: (
    name = "title",
    label = "Title"
  ): Omit<TextFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "text",
    required: true,
  }),

  description: (
    name = "description",
    label = "Description"
  ): Omit<TextareaFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "textarea",
    rows: 4,
  }),

  status: (
    name = "status",
    label = "Status",
    options: Array<{value: string | number; label: string}>
  ): Omit<SelectFieldProps, "onChange" | "value" | "error"> => ({
    name,
    label,
    type: "select",
    options,
    required: true,
  }),
};
export function getFieldValue(
  field: FormFieldProps,
  values: Record<string, unknown>
): any {
  const rawValue = values[field.name];

  switch (field.type) {
    case "text":
    case "email":
    case "password":
    case "url":
    case "tel":
    case "textarea":
    case "date":
    case "datetime-local":
    case "time":
    case "richtext":
      return typeof rawValue === "string" ? rawValue : "";

    case "number":
      return typeof rawValue === "number" ? rawValue : undefined;

    case "select":
    case "radio":
      return typeof rawValue === "string" || typeof rawValue === "number"
        ? rawValue
        : "";

    case "multiselect":
    case "checkbox-group":
      return Array.isArray(rawValue) ? rawValue : [];

    case "checkbox":
    case "switch":
      return typeof rawValue === "boolean" ? rawValue : false;

    case "file":
      return Array.isArray(rawValue) && rawValue.every((f) => f instanceof File)
        ? rawValue
        : [];

    default:
      return rawValue;
  }
}
